/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useExtensionsList.ts
 🗓️ Created: 2025-01-15
 🧩 Description:
    Custom hook for managing extensions list with DataGrid integration.
────────────────────────────────────────────────────────────────*/

import { useCallback, useMemo, useEffect } from 'react';
import { useDataGrid, type DataGridFetchResult } from '../../../hooks/useDataGrid';
import { fetchExtensions, type ListParams } from '../api/extensionsApi';
import type { ExtensionColumn } from '../types';

interface UseExtensionsListOptions {
  initialColumns: ExtensionColumn[];
  initialSortBy?: string;
  initialSortOrder?: 'asc' | 'desc';
  initialLimit?: number;
  domainId?: number;
}

export function useExtensionsList(options: UseExtensionsListOptions) {
  const { initialColumns, initialSortBy = 'extension', initialSortOrder = 'asc', initialLimit = 10, domainId } = options;

  const sortableColumnKeys = useMemo(() => {
    return new Set(initialColumns.filter(column => column.sortable).map(column => column.key));
  }, [initialColumns]);

  const fetchData = useCallback(async (params: {
    page: number;
    limit: number;
    sortBy: string;
    sortOrder: 'asc' | 'desc';
    search?: string;
  }) => {
    const safeSortBy = params.sortBy && sortableColumnKeys.has(params.sortBy)
      ? params.sortBy
      : initialSortBy;
    const safeSortOrder = params.sortOrder === 'desc' ? 'desc' : 'asc';

    const response = await fetchExtensions({
      page: params.page,
      limit: params.limit,
      sortBy: safeSortBy,
      sortOrder: safeSortOrder,
      search: params.search?.trim(),
      domainId, // Pass domainId to filter by domain
    });
    return {
      data: response.data || [],
      pagination: response.pagination || {
        page: params.page,
        limit: params.limit,
        total: 0,
        totalPages: 0
      }
    } as DataGridFetchResult;
  }, [sortableColumnKeys, initialSortBy, domainId]);

  const dataGrid = useDataGrid({
    fetchData,
    initialColumns,
    initialSortBy,
    initialSortOrder,
    initialLimit,
  });

  // Refresh data when domainId changes
  useEffect(() => {
    if (domainId !== undefined) {
      dataGrid.refresh();
    }
  }, [domainId]);

  return dataGrid;
}

